import { ref, computed } from 'vue'
import {
  doc,
  getDoc,
  setDoc,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  runTransaction,
  limit,
  updateDoc
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'
import { useAuth } from '@/composables/common/useAuth'

// 충전 패키지 정의 (1:1 비율 + 보너스)
export const chargePackages = [
  { amount: 10000, price: 10000, bonus: 0, label: '1만원' },
  { amount: 30000, price: 30000, bonus: 1000, label: '3만원', bonusRate: '3%' },
  { amount: 50000, price: 50000, bonus: 2500, label: '5만원', bonusRate: '5%' },
  { amount: 100000, price: 100000, bonus: 7000, label: '10만원', bonusRate: '7%' },
  { amount: 300000, price: 300000, bonus: 30000, label: '30만원', bonusRate: '10%' },
  { amount: 500000, price: 500000, bonus: 60000, label: '50만원', bonusRate: '12%' },
  { amount: 1000000, price: 1000000, bonus: 150000, label: '100만원', bonusRate: '15%' }
]

// 계좌 정보
export const bankInfo = {
  bankName: '카카오뱅크',
  accountNumber: '3333-31-5321054',
  accountHolder: '심응선(이에스랩스 코퍼레이션)'
}

export function usePoint() {
  const { user } = useAuth()

  const balance = ref(0)
  const transactions = ref([])
  const chargeRequests = ref([])
  const loading = ref(false)
  const error = ref(null)

  // 포인트 잔액 조회
  const loadBalance = async () => {
    if (!user.value) return

    try {
      loading.value = true
      const pointDoc = await getDoc(doc(db, 'partnerPoints', user.value.uid))

      if (pointDoc.exists()) {
        balance.value = pointDoc.data().balance || 0
      } else {
        // 최초 접근 시 포인트 문서 생성
        await setDoc(doc(db, 'partnerPoints', user.value.uid), {
          balance: 0,
          totalCharged: 0,
          totalUsed: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
        balance.value = 0
      }
    } catch (err) {
      console.error('포인트 잔액 로드 실패:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  // 거래 내역 조회
  const loadTransactions = async (limitCount = 50) => {
    if (!user.value) return

    try {
      loading.value = true
      const q = query(
        collection(db, 'pointTransactions'),
        where('partnerId', '==', user.value.uid),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      )

      const snapshot = await getDocs(q)
      transactions.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('거래 내역 로드 실패:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  // 충전 신청 내역 조회 (파트너용)
  const loadMyChargeRequests = async () => {
    if (!user.value) return

    try {
      const q = query(
        collection(db, 'pointChargeRequests'),
        where('partnerId', '==', user.value.uid),
        orderBy('createdAt', 'desc'),
        limit(20)
      )

      const snapshot = await getDocs(q)
      chargeRequests.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('충전 신청 내역 로드 실패:', err)
    }
  }

  // 충전 신청하기 (계좌이체 방식)
  const requestCharge = async (packageData, depositorName) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      const totalAmount = packageData.amount + (packageData.bonus || 0)

      const docRef = await addDoc(collection(db, 'pointChargeRequests'), {
        partnerId: user.value.uid,
        partnerEmail: user.value.email,
        partnerName: user.value.displayName || depositorName,
        depositorName: depositorName,

        // 패키지 정보
        packageAmount: packageData.amount,
        packagePrice: packageData.price,
        packageBonus: packageData.bonus || 0,
        totalAmount: totalAmount,

        // 상태
        status: 'pending', // pending(대기) → approved(승인) / rejected(거절)

        // 타임스탬프
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      return { success: true, requestId: docRef.id }
    } catch (err) {
      console.error('충전 신청 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 충전 신청 취소 (pending 상태일 때만)
  const cancelChargeRequest = async (requestId) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      const requestRef = doc(db, 'pointChargeRequests', requestId)
      const requestDoc = await getDoc(requestRef)

      if (!requestDoc.exists()) {
        return { success: false, error: '신청 내역을 찾을 수 없습니다.' }
      }

      if (requestDoc.data().status !== 'pending') {
        return { success: false, error: '대기 중인 신청만 취소할 수 있습니다.' }
      }

      if (requestDoc.data().partnerId !== user.value.uid) {
        return { success: false, error: '본인의 신청만 취소할 수 있습니다.' }
      }

      await updateDoc(requestRef, {
        status: 'cancelled',
        updatedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('충전 신청 취소 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 포인트 충전 (어드민에서 호출 - 승인 시)
  const chargePoints = async (partnerId, amount, requestId = null) => {
    try {
      const result = await runTransaction(db, async (transaction) => {
        const pointRef = doc(db, 'partnerPoints', partnerId)
        const pointDoc = await transaction.get(pointRef)

        const currentData = pointDoc.exists() ? pointDoc.data() : {
          balance: 0,
          totalCharged: 0,
          totalUsed: 0
        }

        const newBalance = currentData.balance + amount
        const newTotalCharged = currentData.totalCharged + amount

        // 포인트 잔액 업데이트
        transaction.set(pointRef, {
          ...currentData,
          balance: newBalance,
          totalCharged: newTotalCharged,
          updatedAt: new Date().toISOString()
        }, { merge: true })

        return { newBalance }
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        partnerId: partnerId,
        type: 'charge',
        amount: amount,
        balanceAfter: result.newBalance,
        description: `파트너스 포인트 충전 ${amount.toLocaleString()}P`,
        relatedId: requestId,
        createdAt: new Date().toISOString()
      })

      // 현재 사용자의 충전이면 잔액 업데이트
      if (user.value && user.value.uid === partnerId) {
        balance.value = result.newBalance
      }

      return { success: true, balance: result.newBalance }
    } catch (err) {
      console.error('포인트 충전 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 포인트 사용
  const usePoints = async (amount, description, relatedId = null) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      const result = await runTransaction(db, async (transaction) => {
        const pointRef = doc(db, 'partnerPoints', user.value.uid)
        const pointDoc = await transaction.get(pointRef)

        if (!pointDoc.exists()) {
          throw new Error('포인트 정보가 없습니다.')
        }

        const currentBalance = pointDoc.data().balance || 0

        if (currentBalance < amount) {
          throw new Error('포인트가 부족합니다.')
        }

        const newBalance = currentBalance - amount
        const newTotalUsed = (pointDoc.data().totalUsed || 0) + amount

        // 포인트 잔액 업데이트
        transaction.update(pointRef, {
          balance: newBalance,
          totalUsed: newTotalUsed,
          updatedAt: new Date().toISOString()
        })

        return { newBalance }
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        partnerId: user.value.uid,
        type: 'use',
        amount: -amount,
        balanceAfter: result.newBalance,
        description: description,
        relatedId: relatedId,
        createdAt: new Date().toISOString()
      })

      balance.value = result.newBalance
      return { success: true, balance: result.newBalance }
    } catch (err) {
      console.error('포인트 사용 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 포인트 환불
  const refundPoints = async (amount, description, relatedId = null) => {
    if (!user.value) return { success: false, error: '로그인이 필요합니다.' }

    try {
      const result = await runTransaction(db, async (transaction) => {
        const pointRef = doc(db, 'partnerPoints', user.value.uid)
        const pointDoc = await transaction.get(pointRef)

        if (!pointDoc.exists()) {
          throw new Error('포인트 정보가 없습니다.')
        }

        const currentBalance = pointDoc.data().balance || 0
        const newBalance = currentBalance + amount
        const newTotalUsed = Math.max(0, (pointDoc.data().totalUsed || 0) - amount)

        // 포인트 잔액 업데이트
        transaction.update(pointRef, {
          balance: newBalance,
          totalUsed: newTotalUsed,
          updatedAt: new Date().toISOString()
        })

        return { newBalance }
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        partnerId: user.value.uid,
        type: 'refund',
        amount: amount,
        balanceAfter: result.newBalance,
        description: description,
        relatedId: relatedId,
        createdAt: new Date().toISOString()
      })

      balance.value = result.newBalance
      return { success: true, balance: result.newBalance }
    } catch (err) {
      console.error('포인트 환불 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 잔액 충분 여부 확인
  const hasEnoughPoints = computed(() => {
    return (amount) => balance.value >= amount
  })

  return {
    balance,
    transactions,
    chargeRequests,
    loading,
    error,
    loadBalance,
    loadTransactions,
    loadMyChargeRequests,
    requestCharge,
    cancelChargeRequest,
    chargePoints,
    usePoints,
    refundPoints,
    hasEnoughPoints
  }
}
